/**
 * Phase3VerifyOnProductionBody (extracted from ReleaseOpsTab.jsx in iter84.59.21)
 *
 * Body of the Phase 3 ("Verify on production") collapsible section.
 * The `<CollapsiblePhaseSection number="3" ...>` wrapper stays in the
 * orchestrator (consistent with the Phase 2 / Phase 4 extractions in
 * iter84.59.19); only the children move here.
 *
 * Renders, in order:
 *   1. (production only) DeploymentReadinessBanner re-check + auto-flip
 *      of `post_deploy:prod_readiness_green`.
 *   2. (preview only) Manual gate row for `post_deploy:prod_readiness_green`
 *      so the operator can tick it while watching prod in another tab.
 *   3. (preview only) UAT runbook card + gate (`post_deploy:uat_runbook_complete`).
 *   4. Post-deploy Auto-Verify InlineGateCard (`post_deploy:auto_verify_prod`).
 *      Auto-flipped by the Phase 4 verifier card via `Phase4AuditSentinelBody`'s
 *      env-discriminated wiring.
 *   5. (iter86.35) Open Pull Requests card — moved here from Phase 2:
 *      in the dev-branch GitOps flow the PR that matters is the
 *      dev → main PROMOTE merge, opened AFTER production verifies
 *      (3.1/3.2) and recorded in the Finalize card's canon-merge row.
 *
 * Touched in iter84.59.21 only to relocate — ZERO behavior change.
 */
import DeploymentReadinessBanner from "@/components/site-admin/DeploymentReadinessBanner";
import { GateButtons } from "@/components/site-admin/GateButtons";
import { checkOf } from "@/components/site-admin/release-ops/CollapsiblePhaseSection";
import { SubStep } from "@/components/site-admin/release-ops/Phase1PreCommitSafetyBody";

export function Phase3VerifyOnProductionBody({
    gateCtx,
    activePromotion,
    isProd,
    autoFlipGate,
}) {
    return (
        <>
            {/* Prod readiness gate. The DeploymentReadinessBanner above
                already covers preview-side readiness; on production we
                re-render it here so the operator visually re-checks the
                deployed app's signals. */}
            {/* iter84.56.7-f — Production readiness re-check on prod env
                also auto-flips its gate (post_deploy:prod_readiness_green). */}
            {isProd && (
                <DeploymentReadinessBanner
                    onVerdictResolved={({ overall, warn_count, fail_count }) =>
                        autoFlipGate({
                            section: "post_deploy",
                            itemId: "prod_readiness_green",
                            verdict: overall,
                            summary: { warn_count, fail_count },
                        })
                    }
                />
            )}
            {/* iter84.56.7-j — Phase 3 gate rows hidden on production.
                You can't start a promotion on prod (Phase 0 is read-only),
                so these rows would just sit there showing "Start a
                promotion in Phase 0 to gate this" — useless noise.
                Operator ticks these from preview (where finalize lives)
                while watching the production app in another tab. */}
            {!isProd && (
                <SubStep n={1} title="Production readiness" phase={3} accentClass="text-emerald-400">
                    <GateButtons
                        {...gateCtx}
                        section="post_deploy"
                        itemId="prod_readiness_green"
                        label="Production Readiness banner re-checked GREEN"
                        required
                        state={checkOf(activePromotion, "post_deploy", "prod_readiness_green")}
                    />
                </SubStep>
            )}

            {/* iter84.59.15 → iter84.59.16 → iter87.107 — Post-Deploy
                Auto-Verify gate. On PREVIEW it's effectively manual. On
                PRODUCTION the Phase 4 verifier's verdict auto-flips this
                gate: clear → Approved, warn → Waived, fail → stays Pending.
                iter87.107 — dropped the extra InlineGateCard wrapper so
                this SubStep visually matches its siblings (3.1 / 3.3 /
                3.4), per operator design pass. Label carries the whole
                meaning; the "auto-flips on Phase 4" nuance is documented
                in the phase heading and the runbook doc. */}
            <SubStep n={2} title="Post-deploy Auto-Verify" phase={3} accentClass="text-emerald-400">
                <GateButtons
                    {...gateCtx}
                    section="post_deploy"
                    itemId="auto_verify_prod"
                    label={isProd
                        ? "Auto-Verify on production (auto-flips on Phase 4 verdict)"
                        : "Auto-Verify on production (manual on preview)"}
                    required
                    state={checkOf(activePromotion, "post_deploy", "auto_verify_prod")}
                />
            </SubStep>

            {/* iter86.82.3 — Concert Health parity: vendor + game health
                are Phase 3 evidence too (3.3 / 3.4). Evidence lives on
                production's Concert Health tab; ticked here on preview. */}
            {!isProd && (
                <SubStep n={3} title="Vendor health" phase={3} accentClass="text-emerald-400">
                    <GateButtons
                        {...gateCtx}
                        section="post_deploy"
                        itemId="vendor_health_prod"
                        label="Vendor health GREEN on production (Concert Health tab)"
                        required
                        state={checkOf(activePromotion, "post_deploy", "vendor_health_prod")}
                    />
                </SubStep>
            )}
            {!isProd && (
                <SubStep n={4} title="Game health" phase={3} accentClass="text-emerald-400">
                    <GateButtons
                        {...gateCtx}
                        section="post_deploy"
                        itemId="game_health_prod"
                        label="Game health clean on production (Concert Health tab)"
                        required
                        state={checkOf(activePromotion, "post_deploy", "game_health_prod")}
                    />
                </SubStep>
            )}

            {/* iter86.82 — the PROMOTE PR (dev → main) moved OUT of the
                per-iter runbook: the promotion closes right after 3.1 +
                3.2 verify production. The PR review is now the batched
                Canon Sync ritual — see the CanonSyncCard in Phase 4. */}

            {/* iter84.56.7-g — Values Engine is a preview-side validator
                of the new code's static config. Renders once in Phase 1
                (preview-only) and is gated there. NOT re-rendered in
                Phase 3 — the production-side validation surface is the
                Production Readiness banner above (already gated).
                iter86.8 — the post-deploy UAT runbook walk is RETIRED
                (ceremonial: preview IS staging). Acceptance moved to
                Phase 1 as the `preview_acceptance` gate; Phase 3 keeps
                only the programmatic production checks below. */}
        </>
    );
}
